import { useId } from 'react'
import type { JSX, PropsWithChildren } from 'react'

import {
  ChartColumnIcon,
  Heading,
  PageHeader,
  PageLayout,
  Text,
} from '@buffer-mono/popcorn'

import { EmptyStateBanner } from '~publish/components/EmptyStateBanner'
import { SectionAvatar } from '~publish/components/SectionAvatar'

import styles from './InsightsEmptyState.module.css'

function InsightsEmptyStateRoot({ children }: PropsWithChildren): JSX.Element {
  return <PageLayout>{children}</PageLayout>
}

function InsightsEmptyStateHeader({
  children,
}: PropsWithChildren): JSX.Element {
  return (
    <PageHeader separator={false}>
      <PageHeader.Row>
        <PageHeader.TitleGroup>
          <PageHeader.TitleLeading>
            <SectionAvatar>
              <ChartColumnIcon size={24} />
            </SectionAvatar>
          </PageHeader.TitleLeading>
          <PageHeader.Title>Insights</PageHeader.Title>
        </PageHeader.TitleGroup>
      </PageHeader.Row>
      {children}
    </PageHeader>
  )
}

function InsightsEmptyStateContent({
  children,
}: PropsWithChildren): JSX.Element {
  return (
    <PageLayout.Container className={styles.content}>
      {children}
    </PageLayout.Container>
  )
}

function InsightsEmptyStateBanner({
  children,
}: PropsWithChildren): JSX.Element {
  const headingId = useId()

  return (
    <EmptyStateBanner aria-labelledby={headingId}>
      <EmptyStateBanner.Content>
        <Heading as="h2" size="sm" id={headingId}>
          Turn your posts into insights
        </Heading>
        <Text color="subtle">Connect a channel to unlock your insights.</Text>
        {children}
      </EmptyStateBanner.Content>
      <EmptyStateBanner.Decorations>
        <EmptyStateBanner.Decoration>🎯</EmptyStateBanner.Decoration>
        <EmptyStateBanner.Decoration>📈</EmptyStateBanner.Decoration>
        <EmptyStateBanner.Decoration>📊</EmptyStateBanner.Decoration>
        <EmptyStateBanner.Decoration>🔍</EmptyStateBanner.Decoration>
      </EmptyStateBanner.Decorations>
    </EmptyStateBanner>
  )
}

export const InsightsEmptyState = Object.assign(InsightsEmptyStateRoot, {
  Header: InsightsEmptyStateHeader,
  Content: InsightsEmptyStateContent,
  Banner: InsightsEmptyStateBanner,
  Actions: EmptyStateBanner.Actions,
})
